/**
 * @lines98/client/screens/leaderboard-screen — раздел «Лидерборд».
 *
 * ДВУХУРОВНЕВАЯ модель (owner-decision 2026-06-04, extra-modes §5.4 «при росте
 * переключателей → пикер / группировка»). Раньше был ПЛОСКИЙ ряд из 4 переключателей
 * (Доска дня + 3 режима), который на узких экранах переносился на 2-ю строку и не
 * масштабировался к 7 таблицам. Новая структура разделяет две РАЗНЫЕ сущности и
 * масштабируется без переноса/обрезки:
 *
 *   Уровень 1 — `segmented` [ Доска дня | Рекорды ]   (всегда 2, влезают на 320px)
 *   Уровень 2 — контекстный:
 *     · Доска дня → `segmented` [ Сегодня | Вчера ]   (внутренние под-табы, §6)
 *     · Рекорды  → `picker` режима, сгруппированный по семьям как в лобби
 *                  (Обычная игра / Соревновательный / Мета); locked-режимы — «Скоро».
 *                  Пикер растёт списком на любое число режимов (резать/переносить
 *                  нечего — вертикальный список на полную ширину).
 *
 * Модель «ствол → ветки»: экран монтирует ОБЩИЕ кирпичи (`segmented`, `picker`) и
 * наполняет их из реестра режимов (`lib/mode-registry` — единый источник, не копии).
 * Селекторы монтируются ОДИН раз; при переключении меняется только тело таблицы
 * (rows-scroll) — без пере-создания DOM селекторов и потери скролла/фокуса.
 *
 * Источники данных (T-8 / ADR-0013) — через `services/leaderboard-prefetch` (дедуп):
 *   - Доска дня  → `GET /daily/leaderboard` (per Сегодня/Вчера date).
 *   - Рекорды    → `GET /alltime/leaderboard?mode=` / `master-jeweler` (karat-борд).
 *
 * Instant-ui-hydration (анти-flicker, 2026-06-10 — как Мастерская): экран НЕ гейтит
 * видимый список на сеть. (§1) каркас + селекторы монтируются сразу; (§3) при mount
 * синхронно сидим `paneState` из снимка `lib/leaderboard-snapshot` (последняя показанная
 * страница КАЖДОЙ вкладки/режима, ключ = вкладка/режим) → повторный заход рисует строки
 * мгновенно; (§4) boot-префетч стартовой страницы + дедуп in-flight (один запрос на
 * boot-префетч + mount); (§5) холодный заход — стабильный СКЕЛЕТ той же структуры/размера
 * (zero layout-shift, `--awaiting` fade), а не текст-своп «Загружаем…»→строки.
 *
 * **§3.1 Stale-while-revalidate (brief-44 §B, 2026-06-11): кэш + сеть, не кэш ВМЕСТО сети.**
 * Снимок даёт мгновенный кадр, но НЕ отменяет сеть. mount И КАЖДОЕ переключение
 * (`switchTop`/`switchDailyTab`/`switchMode`) И возврат фокуса ВСЕГДА запускают фоновую
 * ревалидацию активной страницы (`revalidatePane` → `loadPane`), независимо тёплый/холодный
 * кэш → `reconcileActivePane` тихо подменяет строки на месте (та же сигнатура = ноль
 * перерисовки). Прежний `ensureLoaded` ранним `return` на статусе 'ready' НИКОГДА не освежал
 * тёплый борд при внутри-приложенческой навигации → новые результаты появлялись лишь после F5
 * (root cause 1, устранён). HTTP-кэш обойдён в `api-client` (`cache:'no-store'`) — фоновый GET
 * реально бьёт сервер (root cause 2). Скелет — ТОЛЬКО холодный путь (снимка нет); тёплая
 * ревалидация без скелета/мерцаний. Делибератные переключения НЕ дросселируются (свежесть,
 * AC-2); троттл (`focusRefreshGate`) — только на focus/visibility-серии.
 *
 * Снимок — UX-проекция: истина всегда следующий fetch (reconcile перетирает server-truth'ом),
 * `short_id`-видимость решает СЕРВЕР (anti-shadow-practice). Лидерборд read-only — кэш
 * ничего не «решает», в проверки/движок не входит. Снимок/дедуп — ОБЩИЕ модули
 * (`lib/screen-snapshot` / `lib/shared-flight`), переиспользованы с метой, не скопированы.
 *
 * Foundational invariant Anti-shadow-practice (DECISIONS.md + core-daily-board.md §6
 * + anti-cheat.md §3A.4): daily LIVE withholds foreign `short_id` → такие строки
 * рендерятся TEASER (locked до rollover); поведение строки определяется ИСКЛЮЧИТЕЛЬНО
 * наличием `short_id` (сервер — единственный источник истины, без клиентского
 * «if today»). All-time строки всегда несут `short_id` → всегда PLAYABLE (AC-9).
 *
 * Mock injection: `mountLeaderboardScreen(host, mockData)` минует daily-сеть
 * (`?demo=leaderboard`). `allTimeMode` → стартовать в разделе «Рекорды» на этом
 * режиме (`?demo=alltime` / deep-links).
 *
 * Spec: extra-modes-alltime.md §5.4 + core-daily-board.md §6. Layout/CSS:
 * styles/leaderboard.css. Кирпичи: components/segmented, components/picker.
 */

import { dateOfDayFromUtc } from '@lines98/shared/util';

import { levelBadgeHtml } from '../components/level-badge.js';
import { createPicker, type PickerGroup, type PickerHandle } from '../components/picker.js';
import { createSegmented, type SegmentedHandle } from '../components/segmented.js';
import { attachAutofit, type AutofitHandle } from '../lib/autofit-text.js';
import { t, getLocale } from '../lib/i18n/index.js';
import { escapeHtml, formatDateLabel, formatRuNumber } from '../lib/format.js';
import type { ContentLang } from '@lines98/shared/config/seo';
import {
  FAMILY_LABEL,
  LOBBY_FAMILIES,
  entriesOfFamily,
  modeLabel,
} from '../lib/mode-registry.js';
import { leaderboardPaneSignature, type PaneSigState } from '../lib/leaderboard-pane-signature.js';
import { installSurfaceReflow } from '../lib/surface-reflow.js';
import { createMinIntervalGate } from '../lib/min-interval-gate.js';
import { loadMetaSnapshot } from '../lib/meta-snapshot.js';
import { createValueGuard } from '../lib/reconcile-guard.js';
import { loadPendingOwnRow, clearPendingOwnRow } from '../lib/pending-own-row.js';
import { bootIdentity } from '../services/identity.js';
import { attachTitleFit } from '../lib/title-fit.js';
import {
  formatHms,
  msUntilNextRollover,
} from '../services/countdown.js';
import {
  getCachedDailySeed,
  getDailySeed,
} from '../services/daily-api.js';
import type { LeaderboardRow } from '../services/leaderboard-api.js';
import { getMetaState } from '../services/meta-api.js';
import {
  dailyPaneKey,
  loadLeaderboardSnapshot,
  pruneStaleDailyPanes,
  saveLeaderboardPane,
  type CachedPane,
  type CachedRowVm,
} from '../lib/leaderboard-snapshot.js';
import { sharedLeaderboardPane } from '../services/leaderboard-prefetch.js';
import { useGameStore } from '../store/game-store.js';
import type { DailyOwnRow, GameMode } from '@lines98/shared/types/game';
import type { ScreenHandle } from './board-screen.js';

// =============================================================================
// Mock data shape — `?demo=leaderboard` viewport tests inject this to bypass
// the daily network. Mirrors the rendered fields (camelCase).
// =============================================================================

export interface LeaderboardMockRow {
  readonly rank: number;
  readonly playerName: string;
  /** `#`-tag part (non-PII suffix). Optional in mocks; default rendered if absent. */
  readonly playerTag?: string;
  /** Player level (display-only badge). Optional in mocks; absent → no badge. */
  readonly level?: number;
  readonly scoreVerified: number;
  readonly movesCount: number;
  readonly isViewer: boolean;
  /** Present → playable; absent → teaser (mirrors server short_id presence). */
  readonly shortId?: string;
}

export interface LeaderboardData {
  readonly dateLabel: string;
  readonly initialTab?: DailyTabId;
  readonly rowsToday: ReadonlyArray<LeaderboardMockRow>;
  readonly rowsYesterday: ReadonlyArray<LeaderboardMockRow>;
}

// =============================================================================
// Top-level sections + daily sub-tabs.
// =============================================================================

/** Top level (уровень 1) — две разные сущности лидерборда. */
type TopId = 'daily' | 'records';

// labelKey → i18n-ключ; реальный текст резолвится per-mount в `t()` (экран ре-монтируется
// на смену языка через app.ts gamePhaseLangChanged → подписи следуют языку).
const TOP_SECTIONS: ReadonlyArray<{ readonly id: TopId; readonly labelKey: string }> = [
  { id: 'daily', labelKey: 'game.lb.tabDaily' },
  { id: 'records', labelKey: 'game.lb.tabRecords' },
];

/** Daily section sub-tabs (live today / full yesterday). */
type DailyTabId = 'today' | 'yesterday';

const DAILY_SUBTABS: ReadonlyArray<{ readonly id: DailyTabId; readonly labelKey: string }> = [
  { id: 'today', labelKey: 'game.lb.tabToday' },
  { id: 'yesterday', labelKey: 'game.lb.tabYesterday' },
];

/**
 * Records picker groups — из реестра режимов (единый источник). Семьи как в лобби
 * (закон Якоба). Выбираемы: играбельные режимы (есть `mode`) И любые `leaderboardOnly`
 * ranking-витрины (сейчас таких нет — карат-борд убран owner-directive 2026-06-25;
 * условие сохранено generic'ом на будущее). locked-режимы — ПРОСТО приглушены и
 * неактивны, БЕЗ ярлыка «Скоро» (owner-playtest 2026-06-06 item 11: на лидерборде у
 * недоступных режимов метки «Скоро» нет — только серый disabled; «Скоро» остаётся в
 * лобби, где режим реально выбирают). Реестр растит пикер сам.
 */
function recordsPickerGroups(): PickerGroup[] {
  return LOBBY_FAMILIES.map((fam) => ({
    label: FAMILY_LABEL[fam],
    options: entriesOfFamily(fam).map((e) => ({
      id: e.id,
      label: modeLabel(e, getLocale() as ContentLang),
      // Selectable iff not locked AND (playable mode OR leaderboard-only board).
      disabled: e.locked || (!e.mode && !e.leaderboardOnly),
      // No «Скоро» badge here — leaderboard shows locked modes greyed only.
    })),
  })).filter((g) => g.options.length > 0);
}

// =============================================================================
// View model — what the table renders, regardless of mock vs real source.
// =============================================================================

interface RowVm {
  readonly rank: number;
  readonly playerName: string;
  /** `#`-tag part (non-PII suffix) — rendered smaller + muted next to the name. */
  readonly playerTag: string;
  /**
   * Player level (display-only badge next to EVERY name, owner-decision 2026-06-11).
   * Server-truth; `null` → no badge (mock omitted it / defensive shape-guard). NOT a
   * ranking key (AC-9). The viewer's OWN row still prefers `viewerLevel` for parity
   * with `/meta/state` (see renderRow) but falls back to this when needed.
   */
  readonly level: number | null;
  readonly scoreVerified: number;
  readonly movesCount: number;
  readonly isViewer: boolean;
  /** Playable iff a short_id is present (server §6.3); else teaser. */
  readonly shortId: string | null;
}

/** Per-pane async lifecycle. */
type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly rows: ReadonlyArray<RowVm> }
  | { readonly status: 'error'; readonly retryable: true };

// Заголовок экрана — reuse существующего ключа game.leaderboard (рейл-навигация);
// геттер, не const → резолвится по активной локали в момент рендера.
const screenTitle = (): string => t('game.leaderboard');

/**
 * Сколько placeholder-строк рисует скелет холодного захода (instant-hydration §5). Это
 * число СТРОК-каркаса (content-count), не CSS-размер — заполняет типовой видимый список
 * без избыточного DOM; реальные строки заменят их при первом server-truth. Высота/отступ/
 * приглушение каждой строки — из CSS-токенов (`--btn-h` / `--awaiting`), не хардкод.
 */
const SKELETON_ROW_COUNT = 8;

/**
 * Потолок индекса строки для каскад-задержки (`--row-i`, leaderboard.css): строки сверх
 * него проявляются с максимальной задержкой, чтобы длинный список не «наливался» слишком
 * долго. Не размер — счётчик строк для тайминга каскада (owner-decision 2026-06-11).
 */
const REVEAL_ROW_CAP = 12;

/**
 * Уровень зрителя (server-truth `progression.level`), для бейджа рядом со СВОИМ ником
 * в строке лидерборда (Q-PL-A owner «это достижение игрока»). Display-only — НЕ
 * влияет на сортировку/ранг (AC-9). `null` до загрузки / для гостя → бейдж не рисуется.
 * Серверные leaderboard-эндпоинты не несут per-row уровень, поэтому бейдж показываем
 * только на собственной строке (свой уровень известен из `GET /meta/state`). Module-
 * scope (как другие render-параметры экрана; screen — single-instance в shell).
 */
let viewerLevel: number | null = null;

/**
 * Dismiss fn of the currently-open row-locked popover (or null). Module-scope so
 * `unmount()` can fully tear it down — its `document` click-listener + interval +
 * auto-dismiss timer otherwise outlive the screen if the player leaves within the 3s
 * window before auto-dismiss (not a growth leak, but a listener not scoped to the
 * screen). One popover at a time → one ref. Screen is single-instance in the shell.
 */
let activeRowLockedDismiss: (() => void) | null = null;

// MS per rollover day — «Вчера» = today − 1 day (21:00-UTC boundary, server math).
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Минимальный интервал между АВТО-refresh активной страницы по возврату фокуса/вкладки
 * (свежесть лидерборда, owner-decision 2026-06-11 «обновление при открытии + возврате
 * фокуса», НЕ постоянный опрос/websocket). Анти-спам: частые focus/visibilitychange-события
 * (alt-tab дёрганье, оконные менеджеры) НЕ должны бить по сети чаще раза в это окно. Это
 * ТАЙМИНГ-константа поведения (не CSS-размер) → именованная, не magic-число. ~10 с — заметно
 * свежо для «кто-то сыграл, пока смотрю», но без флуда; дедуп параллельных полётов уже даёт
 * `sharedLeaderboardPane`, троттл — поверх него против частоты событий.
 */
const FOCUS_REFRESH_MIN_INTERVAL_MS = 10_000;

/**
 * Свежий «сегодняшний» `date_of_day` доски дня (rollover-fix 2026-06-11). Из тёплого
 * seed-кэша (само-инвалидируется на rollover — `getCachedDailySeed`), иначе СВЕЖИМ
 * `dateOfDayFromUtc(now)`. Вызывается на mount И на visibilitychange/focus — НЕ
 * кэшируется на boot, иначе через полночь «today» залипает (помогает лишь хард-ресет).
 */
function resolveTodayDateOfDay(now: number = Date.now()): string {
  return getCachedDailySeed(now)?.dateOfDay ?? dateOfDayFromUtc(now);
}

/**
 * Records selection — играбельный режим (`GameMode`). Раньше тип включал ещё борд
 * по каратам («Мастер-ювелир»), убранный owner-directive 2026-06-25; теперь records-
 * выбор = просто режим. Алиас сохранён ради читаемости вызовов/документации.
 */
type RecordsId = GameMode;

/**
 * Key for the per-pane state cache.
 *
 * **Daily ключ ДАТИРОВАН — `daily:<date_of_day>` (rollover-fix 2026-06-11), НЕ `daily:<tab>`.**
 * Под-таб (today/yesterday) — лишь UI-селектор; ключ кэша/снимка/дедупа несёт ФАКТИЧЕСКУЮ
 * дату (через `dailyPaneKey`). Иначе после смены дня вчерашний снимок под `daily:today`
 * подставлялся бы на новую доску (owner-bug). Records-ключ = просто `RecordsId` (не датируется).
 */
type PaneKey = string;

// =============================================================================
// Mount
// =============================================================================

/**
 * Mount the «Лидерборд» section (two-level: Доска дня | Рекорды).
 *
 * @param host — container.
 * @param mockData — optional daily mock (viewport tests / `?demo=leaderboard`).
 * @param initialRecords — when set, land on the «Рекорды» section with this records
 *   selection (a playable `GameMode`; `?demo=alltime` / deep-links); otherwise land on
 *   «Доска дня».
 */
export function mountLeaderboardScreen(
  host: HTMLElement,
  mockData?: LeaderboardData,
  initialRecords?: RecordsId,
): ScreenHandle {
  host.innerHTML = '';
  const root = document.createElement('div');
  // .screen-root — ствол-класс edge-clearance (safe-area-aware, global.css).
  root.className = 'leaderboard-screen screen-root';
  host.appendChild(root);

  let activeTop: TopId = initialRecords ? 'records' : 'daily';
  let activeDailyTab: DailyTabId = mockData?.initialTab ?? 'today';
  // Records selection: a playable mode.
  let activeMode: RecordsId = initialRecords ?? 'predict';
  let titleFit: AutofitHandle | null = null;
  let destroyed = false;

  // --- Уровень-бейдж зрителя у своего ника: МГНОВЕННО из снимка меты (instant-hydration
  //     §3 — ТОТ ЖЕ снимок, что Мастерская/Профиль), затем reconcile из getMetaState.
  //     Снимок keyed по playerId → гость/первый заход = null (бейдж не рисуется, graceful).
  //     Это убирает «вщёлкивание» бейджа после сети на первом заходе. ---
  viewerLevel = loadMetaSnapshot()?.progression.level ?? null;
  // Idempotent reconcile: перерисовываем тело борда из-за уровня ТОЛЬКО когда server-truth
  // уровня реально отличается от показанного (== снимку → no-op, бейдж не моргает).
  const viewerLevelGuard = createValueGuard<number | null>();
  viewerLevelGuard.changed(viewerLevel); // зафиксировать стартовое (из снимка) как показанное

  // Resolve the board day for the daily section labels + fetches — РЕАКТИВНО, не
  // зафиксировано на mount (rollover-fix 2026-06-11). Раньше «today» вычислялся ОДИН раз
  // при mount → в открытой вкладке через полночь залипал на вчерашней дате (помогал только
  // хард-ресет). Теперь `resolveDays()` пересчитывает свежим `dateOfDayFromUtc(now)` на
  // mount И на visibilitychange/focus; при детекте смены дня — ресинк (новые ключи/лейблы/
  // fetch + prune вчерашнего daily-снимка). seed-кэш само-инвалидируется на rollover.
  let todayDateOfDay = mockData ? '' : resolveTodayDateOfDay();
  let yesterdayDateOfDay = mockData ? '' : dateOfDayFromUtc(Date.now() - MS_PER_DAY);

  // Per-pane state cache. Daily panes are synchronously «ready» in mock mode;
  // real daily + every all-time mode start «loading» and self-fetch on activation.
  // Daily-ключи ДАТИРОВАНЫ (`daily:<date>`); mock-режим грузит обе под-вкладки по их
  // текущим датам (today/yesterday), чтобы переключатель Сегодня/Вчера их находил.
  const paneState: Partial<Record<PaneKey, LoadState>> = {};
  if (mockData) {
    // В mock-режиме даты не важны (сеть не зовётся) — кладём по ТЕКУЩИМ датам, как их
    // вернёт `dailyKeyForTab`, чтобы под-вкладки совпали с ключами.
    todayDateOfDay = dateOfDayFromUtc(Date.now());
    yesterdayDateOfDay = dateOfDayFromUtc(Date.now() - MS_PER_DAY);
    paneState[dailyKeyForTab('today')] = { status: 'ready', rows: mockData.rowsToday.map(mockToVm) };
    paneState[dailyKeyForTab('yesterday')] = { status: 'ready', rows: mockData.rowsYesterday.map(mockToVm) };
  } else {
    // --- Оптимистичный снимок (instant-ui-hydration §3): синхронно сидим paneState
    //     ПОСЛЕДНЕЙ показанной страницей КАЖДОЙ вкладки/режима → повторный заход рисует
    //     строки СРАЗУ, без «Загружаем…». Истина — следующий fetch (reconcile перетирает
    //     server-truth'ом, AC анти-чита: short_id-видимость с сервера). Гость → снимок
    //     пуст (keyed по playerId). ---
    // Сначала ВЫБРОСИТЬ устаревшие daily-страницы (вчера и старше) из снимка — после
    // rollover старый `daily:<вчера>` не должен залипать (rollover-fix 2026-06-11; brief
    // item 3 — инвалидация при смене дня). Сохраняем только сегодня/вчера + все all-time.
    pruneStaleDailyPanes([todayDateOfDay, yesterdayDateOfDay]);
    const snapshot = loadLeaderboardSnapshot();
    for (const [key, pane] of Object.entries(snapshot?.panes ?? {})) {
      paneState[key as PaneKey] = cachedPaneToLoadState(pane);
    }

    // --- P2 (brief-44 §B): оптимистичная СВОЯ строка только что отправленной доски дня.
    //     SWR ловит mount/focus, но НЕ событие «мой сабмит» → без этого свежий результат
    //     появлялся лишь после fetch. Берём записанную после finish-game серверную свою
    //     строку (`lib/pending-own-row`, server-truth `me` — P1) для СЕГОДНЯШНЕЙ доски и
    //     вмёрживаем в today-страницу ПОВЕРХ снимка: видно мгновенно. SWR на mount тут же
    //     ревалидирует → reconcile сходится с live-бордом (идемпотентно по сигнатуре —
    //     серверная своя строка == оптимистичной → no-op, без мерцания). Гость/нет сабмита
    //     → null → ничего. Только today (вчера/режимы не трогаем). ---
    const pendingOwn = loadPendingOwnRow(todayDateOfDay);
    if (pendingOwn) {
      const todayKey = dailyKeyForTab('today');
      const existing = paneState[todayKey];
      const baseRows =
        existing && existing.status === 'ready' ? existing.rows : [];
      paneState[todayKey] = {
        status: 'ready',
        rows: mergeOwnRow(baseRows, ownRowFromDailyOwn(pendingOwn)),
      };
    }
  }

  const dailyTabDateLabel = (tab: DailyTabId): string => {
    if (mockData) return mockData.dateLabel;
    return formatDateLabel(tab === 'today' ? todayDateOfDay : yesterdayDateOfDay);
  };

  // Дата под-вкладки (today/yesterday) → ДАТИРОВАННЫЙ ключ кэша/снимка `daily:<date>`.
  // Единый источник ключа с прогревом (`leaderboard-prefetch.dailyPaneKey`) — не расходятся.
  function dailyKeyForTab(tab: DailyTabId): PaneKey {
    return dailyPaneKey(tab === 'today' ? todayDateOfDay : yesterdayDateOfDay);
  }

  const currentPaneKey = (): PaneKey =>
    activeTop === 'daily' ? dailyKeyForTab(activeDailyTab) : activeMode;

  const paneStateOrLoading = (key: PaneKey): LoadState =>
    paneState[key] ?? { status: 'loading' };

  /**
   * Последняя ОТРИСОВАННАЯ сигнатура содержимого ПО КЛЮЧУ страницы (idempotent reconcile,
   * owner-bug 2026-06-11 «мелькание при старте»). `renderBody` после отрисовки записывает
   * сюда сигнатуру текущей страницы; `reconcileActivePane` (из `loadPane`) перерисовывает
   * ТОЛЬКО когда server-truth отличается от записанной → совпало (== снимку) = no-op (нет
   * rebuild, нет мелькания, `nameFit` не дёргается). Per-pane, т.к. у каждой вкладки/режима
   * своя страница; переключение вкладок рисует другой ключ (своя сигнатура — рисуем).
   * Только UX-гейт показа: server-truth не нарушается (отличие → перерисовка).
   */
  const lastRenderedSig = new Map<PaneKey, string>();

  /**
   * Статус ПОСЛЕДНЕЙ отрисовки по ключу (для плавного cold-reveal, owner-bug 2026-06-11
   * «лидерборд мелькает при СТАРТЕ»). Холодный заход рисует СКЕЛЕТ (`loading`), затем
   * первый server-truth заменяет его реальными строками. Раньше новая таблица рождалась
   * сразу на ПОЛНОЙ яркости (старая скелет-таблица с `--awaiting` уничтожалась → CSS-
   * transition opacity не на чем срабатывать) = резкий «хлопок». Зная, что ПРЕДЫДУЩИЙ
   * рендер этого ключа был скелетом (`loading`), мы рождаем новую таблицу ПРИГЛУШЁННОЙ
   * (`--awaiting`) и в след. кадре снимаем класс → opacity 0.45→1 плавно проявляется
   * (instant-ui-hydration §5 как задумано). Тёплый путь (снимок есть → сразу `ready`,
   * prev=undefined) и reconcile-no-op (prev=`ready`) НЕ получают fade. Per-pane.
   */
  const lastRenderedStatus = new Map<PaneKey, LoadState['status']>();

  /** `LoadState` экрана → вход сигнатуры (`error` несёт строки, но в сигнатуре — только статус). */
  const toSigState = (state: LoadState): PaneSigState => {
    if (state.status === 'error') return { status: 'error' };
    return state;
  };

  /**
   * Cold-reveal КАСКАДОМ (owner-decision 2026-06-11 «строки по очереди»; фейд всей таблицы
   * целиком owner отверг «не очень»). Условие — предыдущая отрисовка этого ключа была
   * скелетом (`loading`) И новая несёт реальные строки (`ready`). Тёплый/
   * reconcile путь сюда не попадает. Механика: вешаем `--revealing` на новую таблицу →
   * каждая `.leaderboard-row` проявляется ПО ОЧЕРЕДИ сверху вниз (CSS animation + per-row
   * `--row-i` delay, см. leaderboard.css), список «наливается». Класс живёт на эфемерной
   * таблице (следующий рендер пересоздаёт тело без него); анимация `backwards` сама
   * возвращает строки в норму по завершении → снимать класс не нужно. `prefers-reduced-
   * motion` → CSS отключает анимацию (мгновенно). Zero layout-shift: скелет занимал ту же
   * геометрию.
   */
  const playColdRevealIfNeeded = (key: PaneKey, newStatus: LoadState['status']): void => {
    const wasSkeleton = lastRenderedStatus.get(key) === 'loading';
    const nowReal = newStatus === 'ready';
    if (!wasSkeleton || !nowReal) return;
    const table = rowsScroll.querySelector<HTMLElement>('.leaderboard-table');
    if (!table) return;
    // Каскад: per-row CSS-анимация с нарастающей задержкой (--row-i). Только холодный путь.
    table.classList.add('leaderboard-table--revealing');
  };

  // ---------------------------------------------------------------------------
  // Stable shell — built ONCE (селекторы-кирпичи не пересоздаются).
  // ---------------------------------------------------------------------------
  const header = document.createElement('header');
  header.className = 'leaderboard-header';
  const titleRow = document.createElement('div');
  titleRow.className = 'leaderboard-title-row';
  const titleEl = document.createElement('h2');
  titleEl.className = 'leaderboard-title screen-title';
  titleRow.appendChild(titleEl);
  header.appendChild(titleRow);

  // Уровень 1 — segmented [ Доска дня | Рекорды ].
  const topSeg: SegmentedHandle = createSegmented({
    items: TOP_SECTIONS.map((s) => ({ id: s.id, label: t(s.labelKey) })),
    activeId: activeTop,
    ariaLabel: t('game.lb.sectionAria'),
    className: 'leaderboard-top',
    onChange: (id) => switchTop(id as TopId),
  });

  // Уровень 2 — контейнер под контекстный селектор (под-табы ИЛИ пикер режима).
  const subSlot = document.createElement('div');
  subSlot.className = 'leaderboard-sub';

  // Daily sub-tabs (segmented) — показываются на «Доска дня».
  const dailyTabs: SegmentedHandle = createSegmented({
    items: DAILY_SUBTABS.map((sub) => ({ id: sub.id, label: t(sub.labelKey) })),
    activeId: activeDailyTab,
    ariaLabel: t('game.lb.periodAria'),
    className: 'leaderboard-daily-tabs',
    onChange: (id) => switchDailyTab(id as DailyTabId),
  });

  // Records mode picker — показывается на «Рекорды».
  const modePicker: PickerHandle = createPicker({
    groups: recordsPickerGroups(),
    activeId: activeMode,
    ariaLabel: t('game.lb.modeAria'),
    triggerPrefix: t('game.lb.modePrefix'),
    className: 'leaderboard-mode-picker',
    onChange: (id) => switchMode(id as RecordsId),
  });

  subSlot.append(dailyTabs.element, modePicker.element);

  // Тело таблицы — меняется при переключении (стабильный контейнер).
  const rowsScroll = document.createElement('div');
  // data-table-scroll — общий «хром» таблицы (components/data-table): контейнер-панель +
  // bounded-scroll. leaderboard-rows-scroll держит только своё (grid-area).
  rowsScroll.className = 'leaderboard-rows-scroll data-table-scroll';
  rowsScroll.setAttribute('role', 'region');
  rowsScroll.setAttribute('aria-label', t('game.lb.listAria'));

  root.append(header, topSeg.element, subSlot, rowsScroll);

  // Math-триггер «навигация вбок» (owner-decision 2026-06-25): на коротком ландшафте,
  // где в стеке влезает < 7 строк, обвязка уезжает в правую колонку, таблица — на всю
  // высоту (общий lib/surface-reflow + grid-remap в leaderboard.css). 3 nav-блока
  // (header/top/sub); listOverheadM=4 (строка-заголовок колонок таблицы + scroll padding).
  // Ставит data-reflow на root, держит актуальным при ресайзе/повороте.
  const reflowTeardown = installSurfaceReflow(root, { navRows: 3, listOverheadM: 4 });

  // Autofit ника в строке (email-registration §5.2 / AC-13): server-formed ник
  // (часть .leaderboard-row-name-text) может быть длиннее колонки → УМЕНЬШАЕМ шрифт
  // (line-mode), не режем «…» (owner-rule 2026-06-04 «запрещён перенос и …»).
  // Фитим ТОЛЬКО имя — `#tag` (.leaderboard-row-tag) рядом, мельче+тихим (0.8em),
  // не шринкуется (flex 0 0 auto) → тег никогда не уезжает/не обрезается
  // (owner-decision 2026-06-09). capToDesign — не раздуваем сверх дизайн-размера.
  // Своя строка («Вы» + бейдж, без тега) исключена. minPx — читаемость-floor.
  // Attach ОДИН раз на стабильный контейнер; refit() после re-render тела + ресайз.
  // Последний рубеж, если имя всё равно шире floor'а — существующий mask-fade
  // (.leaderboard-row-name-text, не ellipsis — owner-ban) угасает без обрезки.
  const nameFit: AutofitHandle = attachAutofit(rowsScroll, [
    {
      selector: '.leaderboard-row:not(.leaderboard-row--viewer) .leaderboard-row-name-text',
      mode: 'line',
      capToDesign: true,
      minPx: 11,
      // floor-overflow → класс `.is-clamped` навешивает mask-fade ТОЛЬКО когда ник
      // шире колонки даже при minPx (owner-eye-check 2026-06-09): короткий ник (box =
      // ширине текста при flex:0 1 auto) НЕ угасает хвостом; длинный на узком — мягко
      // угасает (НЕ «…», НЕ перенос). См. .leaderboard-row-name-text.is-clamped в CSS.
      clampClass: 'is-clamped',
    },
  ]);

  // ---------------------------------------------------------------------------
  // Render — only the title + sub-selector visibility + body change on switch.
  // ---------------------------------------------------------------------------
  const refreshTitle = (): void => {
    // Заголовок НЕ дублирует селекторы (раздел и режим уже видны в segmented/picker
    // прямо под ним) → короткий, в одну строку (owner-rule «запрещён перенос»).
    // Daily несёт уникальную деталь — дату доски; у рекордов такой детали нет.
    titleEl.textContent =
      activeTop === 'daily'
        ? `${screenTitle()} · ${dailyTabDateLabel(activeDailyTab)}`
        : screenTitle();
    titleFit?.refit();
  };

  const refreshSubSelector = (): void => {
    const isDaily = activeTop === 'daily';
    dailyTabs.element.hidden = !isDaily;
    modePicker.element.hidden = isDaily;
  };

  const renderBody = (): void => {
    if (destroyed) return;
    const key = currentPaneKey();
    const state = paneStateOrLoading(key);
    rowsScroll.innerHTML = renderBodyHtml(state, activeTop, activeDailyTab);
    wireRetry();
    wireRows();
    // Холодный переход скелет→первые-строки → плавное проявление (instant-hydration §5;
    // owner-bug «мелькание при старте»). Тёплый/reconcile путь сюда не попадает. Должно
    // быть ПОСЛЕ innerHTML (нужна новая `.leaderboard-table`) и ДО записи нового статуса.
    playColdRevealIfNeeded(key, state.status);
    // Перефитить ники после пере-render тела (новые .leaderboard-row-name-text).
    nameFit.refit();
    // Запомнить ЧТО сейчас отрисовано для этого ключа (включая `viewerLevel` бейджа) →
    // следующий reconcile с тем же содержимым = no-op (idempotent reconcile). Все пути
    // отрисовки (switch/retry/viewerLevel/rollover) проходят сюда → guard всегда в синке.
    lastRenderedSig.set(key, leaderboardPaneSignature(toSigState(state), viewerLevel));
    lastRenderedStatus.set(key, state.status);
  };

  /**
   * Idempotent reconcile АКТИВНОЙ страницы (вызывается из `loadPane` после fetch). Рисует
   * ТОЛЬКО когда `key` всё ещё активен И сигнатура нового содержимого отличается от уже
   * отрисованной (owner-bug 2026-06-11): совпало (server-truth == показанному снимку) →
   * no-op — нет полного `innerHTML`-rebuild, нет мелькания. Отличается → `renderBody`
   * (обновление видно — желаемо: новый игрок / счёт / уровень). НЕ гейтит switch/retry —
   * только эту reconcile-перерисовку (бриф: гейтим reconcile, не switch).
   */
  const reconcileActivePane = (key: PaneKey): void => {
    if (destroyed || key !== currentPaneKey()) return;
    const nextSig = leaderboardPaneSignature(toSigState(paneStateOrLoading(key)), viewerLevel);
    if (lastRenderedSig.get(key) === nextSig) return; // server-truth == показанному → no-op.
    renderBody();
  };

  const switchTop = (id: TopId): void => {
    if (id === activeTop) return;
    activeTop = id;
    refreshSubSelector();
    refreshTitle();
    renderBody();
    // SWR: ВСЕГДА ревалидируем активную страницу при переключении (даже на тёплом кэше),
    // чтобы новые результаты появлялись без F5 (instant-ui-hydration §3.1 / AC-2).
    revalidatePane(currentPaneKey());
  };

  const switchDailyTab = (id: DailyTabId): void => {
    if (id === activeDailyTab) return;
    activeDailyTab = id;
    refreshTitle();
    renderBody();
    revalidatePane(currentPaneKey());
  };

  const switchMode = (mode: RecordsId): void => {
    if (mode === activeMode) return;
    activeMode = mode;
    refreshTitle();
    renderBody();
    revalidatePane(currentPaneKey());
  };

  const wireRetry = (): void => {
    rowsScroll.querySelector<HTMLButtonElement>('.leaderboard-retry')?.addEventListener('click', () => {
      const key = currentPaneKey();
      paneState[key] = { status: 'loading' };
      renderBody();
      void loadPane(key);
    });
  };

  // Row interaction: ONLY the icon-cell is clickable (owner-directive 2026-05-26).
  // Behaviour from server-shape (short_id presence): playable → open replay;
  // teaser (daily live foreign rows) → in-row locked popover.
  const wireRows = (): void => {
    rowsScroll.querySelectorAll<HTMLElement>('.leaderboard-row').forEach((row) => {
      const iconCell = row.querySelector<HTMLElement>('.leaderboard-row-icons');
      if (!iconCell) return;
      iconCell.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = e.target as HTMLElement;
        if (target.closest('.row-locked-popover')) return;
        const mode = row.dataset.clickableMode;
        const shortId = row.dataset.shortId;
        if (mode === 'teaser') {
          showRowLockedPopover(row);
        } else if (mode === 'playable' && shortId) {
          openReplay(shortId);
        }
      });
      iconCell.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          iconCell.click();
        }
      });
    });
  };

  // ---------------------------------------------------------------------------
  // Fetch
  // ---------------------------------------------------------------------------
  /**
   * Stale-while-revalidate ревалидация активной страницы (instant-ui-hydration §3.1,
   * brief-44 §B). **ВСЕГДА** уходит фоновым `loadPane` — независимо тёплый кэш или
   * холодный. Это замена прежнего `ensureLoaded` («ready → не грузим»), из-за которого
   * тёплый борд НИКОГДА не переспрашивал сервер при внутри-приложенческой навигации и
   * новые результаты не появлялись без F5 (root cause 1).
   *
   * Тёплый путь: снимок уже отрисован `renderBody` (строки видны мгновенно) → этот fetch
   * фоновый → `reconcileActivePane` тихо подменяет на месте (та же сигнатура = ноль
   * перерисовки, §3). Скелета НЕТ (снимок есть). Холодный путь: `renderBody` нарисовал
   * скелет (снимка для ключа нет) → fetch заполняет его cold-reveal каскадом.
   *
   * Анти-спам: дедуп параллельных полётов даёт `sharedLeaderboardPane` (boot-префетч +
   * mount = один запрос). ДЕЛИБЕРАТНЫЕ переключения вкладок/режима НЕ дросселируются
   * (AC-2 — «каждое переключение запускает ревалидацию»; свежесть приоритетна); троттл
   * по min-interval применяется ТОЛЬКО к авто-refresh по фокусу/видимости (`refreshOnFocus`),
   * где события прилетают сериями (alt-tab) — там через `focusRefreshGate`.
   *
   * mock-режим: daily-страницы синхронно `ready` без сети → пропускаем (как делал
   * `ensureLoaded`); реальной сети в mock нет.
   */
  const revalidatePane = (key: PaneKey): void => {
    if (key.startsWith('daily:') && mockData) return;
    void loadPane(key);
  };

  const loadPane = async (key: PaneKey): Promise<void> => {
    // Симптом «свой ник → потом Вы» (owner-bug 2026-06-28): на холодном заходе/F5 экран
    // монтируется и первый daily-fetch уходит ДО резолва личности (`bootIdentity`, IndexedDB
    // async). Без Bearer сервер видит ГОСТЯ → `me`=null + is_own=false → своя строка рисуется
    // ЧУЖИМ ником с закрытым реплеем; затем focus/heartbeat-refresh уже с Bearer подменяет на
    // «Вы». Лечим тем же приёмом, что F5 доски дня (прецедент daily-board/profile/gnomes-screen):
    // ДОЖДАТЬСЯ личности ПЕРЕД daily-fetch → первый же запрос несёт Bearer → сразу «Вы» + свой
    // реплей, без мелькания. Идемпотентен (общий in-flight промис; тёплый заход резолвится
    // мгновенно — не тормозит). Мгновенный кадр из снимка уже отрисован (renderBody на mount) —
    // ждём только фоновый сетевой reconcile. all-time `me`-блока не имеет → личность не ждём.
    if (key.startsWith('daily:')) {
      await bootIdentity();
      if (destroyed) return;
    }
    // server-truth через ОБЩИЙ дедуп-полёт (services/leaderboard-prefetch): boot-префетч
    // «Сегодня» + этот mount-fetch дедупятся в ОДИН запрос; переключение вкладок — свой
    // полёт по ключу. Просто более ранний/недублированный легальный GET (анти-чит цел).
    const paneRes = await sharedLeaderboardPane(paneFetchFor(key));
    if (destroyed) return;

    // Экран запрашивает ТОЛЬКО score-источники (daily / alltime) — карат-борд убран из
    // видимого лидерборда (owner-directive 2026-06-25). `PaneResult` всё ещё union с
    // `karat` (сервис-слой держит дремлющую поддержку — серверный эндпоинт цел), поэтому
    // сужаем дискриминантом: karat сюда не приходит (paneFetchFor его не строит).
    if (paneRes.kind !== 'score') return;
    const result = paneRes.result;
    if (result.ok) {
      let rows = result.rows.map(serverToVm);
      // P2/P3 (brief-44 §B): для daily-страницы вмёрживаем server-truth свою строку из
      // `me`-блока (read-эндпоинт всегда несёт её, даже если ранг ниже страницы) — чтобы
      // своя строка НЕ исчезала при reconcile (page-срез top-N мог её не содержать) и
      // СХОДИЛАСЬ с оптимистичной (P1). Если своя строка УЖЕ в page-rows (`isOwn`) —
      // mergeOwnRow её снимет и поставит одну каноничную (идемпотентно). Только daily;
      // all-time `me`=null (нет per-day своей строки). Гость → me=null → ничего.
      if (key.startsWith('daily:') && result.me) {
        rows = mergeOwnRow(rows, {
          rank: result.me.rank,
          scoreVerified: result.me.scoreVerified,
          movesCount: result.me.movesCount,
          shortId: result.me.shortId,
        });
        // Reconcile подтвердил ЖИВУЮ свою строку (она уходит в снимок ниже, saveLeaderboardPane).
        // `pending-own-row` своё дело сделал (мгновенный показ на ПЕРВОМ mount после финиша) и
        // больше не нужен. Если его НЕ снять — на КАЖДОМ заходе он re-инъектит finish-time ранг
        // (волатилен: другие игроки обгоняют → ранг растёт), а reconcile подменяет на живой →
        // ПОСТОЯННЫЙ прыжок числа ранга (owner-bug 2026-06-28 «скачет при каждом обновлении»).
        // Снимаем ТОЛЬКО для СЕГОДНЯШНЕЙ доски (pending keyed по today); снимок теперь несёт
        // живую свою строку → следующий mount показывает её мгновенно без прыжка. Одноразовость.
        if (key === dailyKeyForTab('today')) clearPendingOwnRow();
      }
      paneState[key] = { status: 'ready', rows };
      saveLeaderboardPane(key, { kind: 'score', rows: rows.map(scoreRowToCached) });
    } else {
      paneState[key] = { status: 'error', retryable: true };
    }
    // Idempotent reconcile активной страницы (== показанному → no-op; отличается → перерисовка).
    reconcileActivePane(key);
  };

  /** Дескриптор запроса для ключа страницы (daily date-key / mode). */
  const paneFetchFor = (key: PaneKey) => {
    // Daily-ключ ДАТИРОВАН — `daily:<date>`; дата = хвост после `daily:` (rollover-fix).
    if (key.startsWith('daily:')) {
      const date = key.slice('daily:'.length);
      return { key, kind: 'daily' as const, date };
    }
    return { key, kind: 'alltime' as const, mode: key as GameMode };
  };

  /**
   * Реактивный ресинк дня (rollover-fix 2026-06-11). Пересчитывает «today»/«yesterday»
   * СВЕЖИМ временем; при детекте смены дня (today изменился): чистит вчерашний daily-снимок
   * (prune), обновляет лейблы/заголовок и ПЕРЕРИСОВЫВАЕТ активную страницу + revalidatePane по
   * НОВОМУ датированному ключу (cache miss → свежий fetch). No-op в mock-режиме (даты фиксированы)
   * и когда день не менялся. Вызывается на mount-tail и на visibilitychange(visible)/focus.
   *
   * @returns `true` если день сменился (значит активная страница уже перезагружена по новому
   *   ключу) → вызывающий focus-handler НЕ дублирует обычный refresh. `false` — день тот же.
   */
  const resyncDayIfChanged = (): boolean => {
    if (mockData || destroyed) return false;
    const freshToday = resolveTodayDateOfDay();
    if (freshToday === todayDateOfDay) return false; // день не менялся → ничего не делаем.
    todayDateOfDay = freshToday;
    yesterdayDateOfDay = dateOfDayFromUtc(Date.now() - MS_PER_DAY);
    // Старый daily-снимок (вчера и старше) больше не нужен — не залипает в localStorage.
    pruneStaleDailyPanes([todayDateOfDay, yesterdayDateOfDay]);
    // Лейблы/заголовок несут дату → обновить; тело — по новому ключу (если активна daily-вкладка).
    refreshTitle();
    if (activeTop === 'daily') {
      renderBody();
      revalidatePane(currentPaneKey());
    }
    return true;
  };

  // Троттл авто-refresh по возврату фокуса/вкладки — не чаще раза в окно (анти-спам частых
  // focus/visibilitychange-серий). Per-mount (свой у каждого захода на экран).
  const focusRefreshGate = createMinIntervalGate(FOCUS_REFRESH_MIN_INTERVAL_MS);

  /**
   * Refresh АКТИВНОЙ страницы по возврату фокуса/вкладки (свежесть, owner-decision 2026-06-11
   * «обновление при открытии + возврате фокуса», НЕ опрос/websocket). Тот же SWR-путь, что
   * mount/переключения — `revalidatePane` (всегда фоновый `loadPane`, тёплый кэш не отменяет
   * сеть). С idempotent reconcile ответ «то же» = ноль мельканий; «появился новый игрок» →
   * строка добавится. Порядок: сначала ресинк дня — если день сменился, страница уже
   * перезагружена по новому ключу → НЕ дублируем. Иначе ТРОТТЛ (анти-спам): в отличие от
   * делибератных переключений (AC-2, не дросселируются), focus/visibilitychange прилетают
   * сериями (alt-tab, оконные менеджеры) → не бьём по сети чаще окна. mock → no-op
   * (`resyncDayIfChanged` гардит mock; `revalidatePane` daily в mock тоже скипается). Гость —
   * снимок пуст, fetch публичный, всё как обычно.
   */
  const refreshOnFocus = (): void => {
    if (mockData || destroyed) return;
    // Смена дня уже перезагрузила активную страницу (новый ключ) → не делать второй fetch.
    if (resyncDayIfChanged()) return;
    // Троттл: частые focus-события не должны бить по сети чаще окна (только этот авто-путь).
    if (!focusRefreshGate.allow()) return;
    revalidatePane(currentPaneKey());
  };

  // Свежий today + refresh активной страницы при возврате во вкладку/фокус (открытый таб мог
  // пережить полночь ИЛИ пока смотрели — кто-то сыграл). НЕ кэшируем «today» на boot.
  const onVisible = (): void => {
    if (document.visibilityState === 'visible') refreshOnFocus();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', refreshOnFocus);

  // «Сердцебиение» живого борда (owner 2026-06-28, симптом «новые результаты только по F5»).
  // Прежде SWR ловил лишь mount + возврат фокуса/вкладки → пока игрок СИДИТ на открытом борде
  // и никуда не уходит, чужие новые результаты не втекали. Лёгкий тик: пока вкладка ВИДИМА,
  // раз в FOCUS_REFRESH_MIN_INTERVAL_MS тихо ревалидируем активную страницу тем же
  // идемпотентным reconcile (нет нового → ноль перерисовки; появился игрок → строка втекает).
  // На входе/возврате обновление МГНОВЕННО (mount-revalidate + onVisible/focus) — тик это ТОЛЬКО
  // ритм уже открытого борда, никакой паузы на входе. НЕ WebSocket/постоянный поток (осознанно:
  // deconstruct 2026-06-12 §P7 — realtime оверкилл для дневного борда на соло-стеке; здесь же
  // переиспользуем готовый flicker-free путь). Скрытая вкладка — ПАУЗА (не бьём по сети/батарее);
  // возврат во вкладку даёт мгновенный refresh через onVisible, тик продолжается. Свой ритм
  // (10с-интервал) → НЕ через `focusRefreshGate` (тот гасит alt-tab-серии, а не штатный тик).
  const heartbeat = (): void => {
    if (mockData || destroyed) return;
    if (document.visibilityState !== 'visible') return;
    if (resyncDayIfChanged()) return; // смена дня уже перезагрузила активную страницу.
    revalidatePane(currentPaneKey());
  };
  const heartbeatTimer = window.setInterval(heartbeat, FOCUS_REFRESH_MIN_INTERVAL_MS);

  // Initial paint.
  refreshSubSelector();
  refreshTitle();
  renderBody();
  titleFit = attachTitleFit(root);
  refreshTitle();

  if (!mockData) {
    void getDailySeed().catch(() => {
      /* best-effort seed refresh — fetches below pass date=/mode= explicitly */
    });
  }
  // SWR on mount: ВСЕГДА ревалидируем активную страницу — даже когда снимок дал тёплый кадр
  // (instant-ui-hydration §3.1). Прежний `ensureLoaded` ранним `return` на 'ready' пропускал
  // fetch на тёплом кэше → новые результаты не появлялись без F5 (root cause 1). Дедуп с boot-
  // префетчем («Сегодня») сохранён — `sharedLeaderboardPane` склеит их в один запрос.
  revalidatePane(currentPaneKey());

  // Fetch the viewer's own level (server-truth) for the badge next to their nick
  // (Q-PL-A). Best-effort: a guest (401) / error leaves `viewerLevel` as the snapshot
  // value (or null). On success, reconcile: repaint ONLY when the server-truth level
  // actually differs from what's shown (== snapshot → no-op, badge doesn't flicker).
  void getMetaState().then((res) => {
    if (destroyed || !res.ok) return;
    const next = res.progression.level;
    if (!viewerLevelGuard.changed(next)) return; // server-truth == shown → no repaint.
    viewerLevel = next;
    renderBody();
  });

  return {
    unmount(): void {
      destroyed = true;
      // Reset module-scope viewer level so a stale value doesn't leak into the next
      // mount (e.g. account switch / re-entry) before the fresh fetch resolves.
      viewerLevel = null;
      // Fully tear down any open row-locked popover (document-listener + interval +
      // auto-dismiss/arm timers) so nothing scoped to this screen outlives it
      // (brief-43 ticket B — player leaves within the 3s auto-dismiss window).
      if (activeRowLockedDismiss) activeRowLockedDismiss();
      // Снять focus/visible слушатели (ресинк дня + refresh активной страницы), иначе
      // переживут экран. Те же ссылки на функции, что регистрировались (onVisible / refreshOnFocus).
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refreshOnFocus);
      // Снять «сердцебиение» живого борда — иначе тик переживёт экран и будет бить по сети.
      clearInterval(heartbeatTimer);
      if (titleFit) titleFit.detach();
      nameFit.detach();
      reflowTeardown();
      topSeg.destroy();
      dailyTabs.destroy();
      modePicker.destroy();
      host.innerHTML = '';
    },
  };
}

// =============================================================================
// Navigation
// =============================================================================

/**
 * Open a replay by short-id — IN-SHELL (store `replay` phase). `app.ts` fetches
 * `GET /api/replay/<shortId>` and branches player/teaser/not-found by server `kind`.
 */
function openReplay(shortId: string): void {
  useGameStore.getState().showReplayById(shortId);
}

// =============================================================================
// View-model mappers
// =============================================================================

function serverToVm(row: LeaderboardRow): RowVm {
  return {
    rank: row.rank,
    playerName: row.playerName,
    playerTag: row.playerTag,
    level: row.level,
    scoreVerified: row.scoreVerified,
    movesCount: row.movesCount,
    isViewer: row.isOwn,
    shortId: row.shortId,
  };
}

/**
 * Минимальная своя строка для merge — общая форма для P1-ответа finish-game
 * (`DailyOwnRow`, snake_case) И read-endpoint `me`-блока (`MyDailyRow`, camelCase).
 * Несём ровно то, что нужно для рендера своей строки (имя = «Вы», бейдж из `viewerLevel`).
 */
interface OwnRowInput {
  readonly rank: number;
  readonly scoreVerified: number;
  readonly movesCount: number;
  readonly shortId: string;
}

/** `DailyOwnRow` (snake_case, finish-game P1) → общий `OwnRowInput`. */
function ownRowFromDailyOwn(me: DailyOwnRow): OwnRowInput {
  return {
    rank: me.rank,
    scoreVerified: me.score_verified,
    movesCount: me.moves_count,
    shortId: me.short_id,
  };
}

/**
 * P2 (brief-44 §B) — вмёрживание СВОЕЙ строки (server-truth) в страницу борда. Чистая
 * функция (тестируемо, без сайд-эффектов). Один источник merge для ДВУХ путей:
 *   - оптимистичный (mount): `me` из finish-game (`lib/pending-own-row`, P1);
 *   - reconcile (SWR-fetch): `me`-блок read-эндпоинта (всегда несёт свою строку, даже если
 *     ранг ниже страницы) — чтобы своя строка НЕ исчезала при reconcile (иначе оптимистичную
 *     затёрло бы page-срезом без неё).
 *
 * Правила (идемпотентность — ключ к «без мерцания»):
 *   1. Удаляем ЛЮБУЮ существующую свою строку (`isViewer`) — серверная заменяет
 *      потенциально устаревшую/дубль из снимка прошлой партии.
 *   2. Строим свою `RowVm` (имя «Вы» рисует `renderRow` по isViewer; tag/level не нужны —
 *      бейдж из `viewerLevel`). 3. Вставляем по `rank`, сортируем по `rank` ASC (порядок
 *      сервера). Оптимистичная и server-truth своя строка СОВПАДАЮТ по сигнатуре → reconcile
 *      no-op (нет мерцания).
 *
 * Возвращает НОВЫЙ массив (вход не мутируется).
 */
export function mergeOwnRow(baseRows: ReadonlyArray<RowVm>, own: OwnRowInput): RowVm[] {
  const withoutOwn = baseRows.filter((r) => !r.isViewer);
  const ownVm: RowVm = {
    rank: own.rank,
    // Имя своей строки рендерится как «Вы» (renderRow ветвит по isViewer) — playerName/
    // playerTag для своей строки не показываются; держим непустыми для type-safety.
    playerName: t('game.lb.you'),
    playerTag: '',
    // Бейдж уровня своей строки берётся из `viewerLevel` (reconciled из /meta/state) —
    // здесь null, чтобы не показать чужой/устаревший уровень.
    level: null,
    scoreVerified: own.scoreVerified,
    movesCount: own.movesCount,
    isViewer: true,
    // Свой short_id (own-replay carve-out) — строка играбельна сразу.
    shortId: own.shortId,
  };
  const merged = [...withoutOwn, ownVm];
  merged.sort((a, b) => a.rank - b.rank);
  return merged;
}

function mockToVm(row: LeaderboardMockRow): RowVm {
  return {
    rank: row.rank,
    playerName: row.playerName,
    playerTag: row.playerTag ?? '0000',
    // Mock may omit level (older fixtures) → null → no badge (defensive).
    level: row.level ?? null,
    scoreVerified: row.scoreVerified,
    movesCount: row.movesCount,
    isViewer: row.isViewer,
    shortId: row.shortId ?? null,
  };
}

// =============================================================================
// Snapshot mappers (instant-ui-hydration §3) — VM ⇄ кэш-строки + кэш → LoadState.
// Кэш-строки несут ровно видимые поля (без `playerId`); `RowVm` — то же подмножество,
// поэтому проекция тривиальна и без дубля рендера.
// =============================================================================

/** Server-truth `RowVm` → кэшируемая строка (для снимка). */
function scoreRowToCached(row: RowVm): CachedRowVm {
  return {
    rank: row.rank,
    playerName: row.playerName,
    playerTag: row.playerTag,
    level: row.level,
    scoreVerified: row.scoreVerified,
    movesCount: row.movesCount,
    isViewer: row.isViewer,
    shortId: row.shortId,
  };
}

/**
 * Кэшируемая страница → `LoadState` для мгновенной отрисовки при mount (reconcile позже).
 * `CachedPane` всё ещё union (score | karat) — снимок-слой держит дремлющую karat-форму;
 * экран рисует ТОЛЬКО score (карат-борд убран из лидерборда owner-directive 2026-06-25),
 * поэтому karat-снимок (если бы оказался) трактуем как пустой `loading` (не рисуем).
 */
function cachedPaneToLoadState(pane: CachedPane): LoadState {
  if (pane.kind !== 'score') return { status: 'loading' };
  return { status: 'ready', rows: pane.rows.map((r) => ({ ...r })) };
}

// =============================================================================
// Table body (rows / header / empty / loading / error) — shared by both sections.
// =============================================================================

function renderBodyHtml(state: LoadState, top: TopId, dailyTab: DailyTabId): string {
  if (state.status === 'loading') return renderSkeleton();
  if (state.status === 'error') return renderError();
  if (state.rows.length === 0) return renderEmptyState(top, dailyTab);
  return renderRows(state.rows);
}

function renderRows(rows: ReadonlyArray<RowVm>): string {
  const headerHtml = `
    <div class="leaderboard-header-row data-table-header" aria-hidden="true">
      <span class="leaderboard-row-rank">${t('game.lb.colRank')}</span>
      <span class="leaderboard-row-name">${t('game.lb.colPlayer')}</span>
      <span class="leaderboard-row-score">${t('game.lb.colScore')}</span>
      <span class="leaderboard-row-moves">${t('game.lb.colMoves')}</span>
      <span class="leaderboard-row-icons"></span>
    </div>
  `;
  return `
    <div class="leaderboard-table data-table">
      ${headerHtml}
      ${rows.map((r, i) => renderRow(r, i)).join('')}
    </div>
  `;
}

function renderRow(row: RowVm, index: number): string {
  // foundational invariant Anti-shadow-practice §3A.4 + spec §6.3: mode decided
  // PURELY by short_id presence (server-enforced). WITH → playable; WITHOUT → teaser.
  const mode: 'playable' | 'teaser' = row.shortId ? 'playable' : 'teaser';
  const dataAttrs = `
    data-clickable-mode="${mode}"
    ${row.shortId ? `data-short-id="${escapeHtml(row.shortId)}"` : ''}
  `;
  // data-table-row — общий хром строки; viewer → +data-table-row--active (выделение «Вы»).
  const cls = `leaderboard-row data-table-row${row.isViewer ? ' leaderboard-row--viewer data-table-row--active' : ''}`;
  // Имя чужой строки = ник (server-formed). Если ник НЕ задан, сервер шлёт ПУСТОЕ имя —
  // обезличенный плейсхолдер локализуем В МОМЕНТ РЕНДЕРА на язык зрителя из каталога
  // (reuse `game.teaser.anonPlayer`: «Игрок»/«Player»/…), а не запекаем по-русски на сервере
  // (owner-bug 2026-06-28). Модульно: новый язык = только JSON, без правок кода. Своя строка
  // — всегда «Вы». Локализация в момент рендера (renderRow перезапускается на смену языка) —
  // запекания языка в снимок нет.
  const foreignName = row.playerName.length === 0 ? t('game.teaser.anonPlayer') : row.playerName;
  const displayName = row.isViewer ? t('game.lb.you') : foreignName;
  const viewerSuffix = row.isViewer ? t('game.lb.youSuffix') : '';
  // Name cell: own row = «Вы» + level-badge (no tag); foreign row = nick at full
  // size + the `#tag` smaller + muted (owner-decision 2026-06-09). Tag styling +
  // sizing live in CSS (.leaderboard-row-tag); HTML only carries the parts.
  const nameCell = row.isViewer
    ? `${escapeHtml(displayName)}`
    : `<span class="leaderboard-row-name-text">${escapeHtml(displayName)}</span>` +
      `<span class="leaderboard-row-tag">#${escapeHtml(row.playerTag)}</span>`;
  // Level badge next to EVERY player's name (display-only, owner-decision 2026-06-11).
  // Server now carries per-row `level`; the viewer's OWN row prefers `viewerLevel`
  // (reconciled from /meta/state, same source) and falls back to the row value, so
  // own + foreign stay consistent. NOT a ranking key (AC-9). Single-source badge
  // markup — reuse `levelBadgeHtml` (no copy / no hardcoded sizes). `null` → no badge.
  const rowLevel = row.isViewer ? (viewerLevel ?? row.level) : row.level;
  const levelBadge = rowLevel !== null ? levelBadgeHtml(rowLevel) : '';
  const iconAriaLabel =
    mode === 'teaser'
      ? t('game.lb.replayLockedAria', { name: foreignName, suffix: viewerSuffix })
      : t('game.lb.replayOpenAria', { name: displayName, score: row.scoreVerified });
  const lockSlot = mode === 'teaser' ? '🔒' : '';
  const iconCell = `
    <span
      class="leaderboard-row-icons"
      role="button"
      tabindex="0"
      aria-label="${escapeHtml(iconAriaLabel)}"
    >
      <span class="leaderboard-row-icon-clap" aria-hidden="true">🎬</span>
      <span class="leaderboard-row-icon-lock" aria-hidden="true">${lockSlot}</span>
    </span>
  `;
  return `
    <div
      class="${cls}"
      style="--row-i:${Math.min(index, REVEAL_ROW_CAP)}"
      ${dataAttrs}
    >
      <span class="leaderboard-row-rank">№${row.rank}</span>
      <span class="leaderboard-row-name">${nameCell}${levelBadge}</span>
      <span class="leaderboard-row-score">${formatRuNumber(row.scoreVerified)}</span>
      <span class="leaderboard-row-moves">${row.movesCount}</span>
      ${iconCell}
    </div>
  `;
}

function renderEmptyState(top: TopId, dailyTab: DailyTabId): string {
  let text: string;
  if (top === 'daily') {
    text =
      dailyTab === 'today'
        ? t('game.lb.emptyToday')
        : t('game.lb.emptyYesterday');
  } else {
    text = t('game.lb.emptyRecords');
  }
  return `<div class="leaderboard-empty-state">${escapeHtml(text)}</div>`;
}

/**
 * Стабильный СКЕЛЕТ холодного захода (instant-ui-hydration §5): вместо текста-свопа
 * «Загружаем…»→строки рисуем каркас таблицы ТОГО ЖЕ размера (тот же header + N
 * placeholder-строк той же высоты `--btn-h`), приглушённый через `--awaiting` (только
 * opacity, GPU-only — НЕ box-shadow/filter, ui-performance §). При первом server-truth
 * `renderBody` заменит его реальными строками → значения плавно проявляются (fade в CSS;
 * `prefers-reduced-motion` уважается). Zero layout-shift: каркас уже занимает место.
 */
function renderSkeleton(): string {
  const headerHtml = `<div class="leaderboard-header-row data-table-header" aria-hidden="true">
         <span class="leaderboard-row-rank">${t('game.lb.colRank')}</span>
         <span class="leaderboard-row-name">${t('game.lb.colPlayer')}</span>
         <span class="leaderboard-row-score">${t('game.lb.colScore')}</span>
         <span class="leaderboard-row-moves">${t('game.lb.colMoves')}</span>
         <span class="leaderboard-row-icons"></span>
       </div>`;
  const rowCells = `<span class="leaderboard-row-rank"></span>
       <span class="leaderboard-row-name"><span class="leaderboard-skel-bar"></span></span>
       <span class="leaderboard-row-score"></span>
       <span class="leaderboard-row-moves"></span>
       <span class="leaderboard-row-icons"></span>`;
  const rows = Array.from({ length: SKELETON_ROW_COUNT }, () =>
    `<div class="leaderboard-row leaderboard-row--skeleton data-table-row" aria-hidden="true">${rowCells}</div>`,
  ).join('');
  return `
    <div class="leaderboard-table data-table leaderboard-table--awaiting" role="status" aria-label="${t('game.lb.loadingAria')}">
      ${headerHtml}
      ${rows}
    </div>
  `;
}

function renderError(): string {
  return `
    <div class="leaderboard-empty-state">
      <p>Не удалось загрузить лидерборд.</p>
      <button class="leaderboard-retry btn" type="button">Повторить</button>
    </div>
  `;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Show an inline locked-replay popover anchored to the clicked row (daily live
 * teaser). Per owner-feedback 2026-05-26: «на той же строке, где жмёшь». One at a
 * time; auto-dismiss on outside click or 3s; countdown ticks every second.
 */
function showRowLockedPopover(row: HTMLElement): void {
  // Tear down any prior popover fully (listener + timers), not just remove the node —
  // one popover at a time, and its document-listener must not survive.
  if (activeRowLockedDismiss) activeRowLockedDismiss();
  document
    .querySelectorAll<HTMLElement>('.row-locked-popover')
    .forEach((el) => el.remove());

  const pop = document.createElement('div');
  pop.className = 'row-locked-popover';
  pop.setAttribute('role', 'status');
  pop.innerHTML =
    '<span class="row-locked-icon" aria-hidden="true">🎬</span>' +
    '<span class="row-locked-text"></span>';
  const textEl = pop.querySelector<HTMLSpanElement>('.row-locked-text');

  const updateText = (): void => {
    if (!textEl) return;
    const ms = msUntilNextRollover(Date.now());
    textEl.textContent =
      ms > 0 ? t('game.lb.countdownIn', { hms: formatHms(ms) }) : t('game.lb.countdownReady');
  };
  updateText();
  row.appendChild(pop);

  const interval = window.setInterval(updateText, 1000);
  const autoDismissTimer = window.setTimeout(() => dismiss(), 3000);

  const dismiss = (): void => {
    clearInterval(interval);
    clearTimeout(autoDismissTimer);
    clearTimeout(armListenerTimer);
    pop.remove();
    document.removeEventListener('click', onOutsideClick, true);
    if (activeRowLockedDismiss === dismiss) activeRowLockedDismiss = null;
  };
  const onOutsideClick = (ev: MouseEvent): void => {
    const t = ev.target as Node;
    if (!pop.contains(t) && !row.contains(t)) dismiss();
  };
  const armListenerTimer = window.setTimeout(() => {
    document.addEventListener('click', onOutsideClick, true);
  }, 0);
  // Expose teardown to unmount() so leaving the screen within the 3s window cleans
  // up the document-listener + timers (brief-43 ticket B).
  activeRowLockedDismiss = dismiss;
}
